import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useLang } from "../../i18n";

const BUBBLES = Array.from({ length: 16 }, (_, i) => ({
  left: 10 + ((i * 53) % 80),
  size: 4 + ((i * 7) % 11),
  delay: (i * 0.41) % 2.6,
  dur: 2.1 + (i % 5) * 0.55,
  drift: ((i % 3) - 1) * 14,
}));

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const [ratio, setRatio] = useState(0.8);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  const fill = useTransform(scrollYProgress, [0, 0.4], [0, 1]);
  const fillHeight = useTransform(fill, (v) => `${v * 58}%`);
  const bubbleOpacity = useTransform(fill, [0.05, 0.25], [0, 1]);
  const pct = useTransform(fill, (v) => `${Math.round(v * 100)}`);

  const cupY = useTransform(scrollYProgress, [0.3, 1], ["0vh", "-42vh"]);
  const cupRotate = useTransform(scrollYProgress, [0.3, 1], [0, -7]);
  const cupOpacity = useTransform(scrollYProgress, [0.85, 0.95], [1, 0]);
  const cupScale = useTransform(scrollYProgress, [0, 0.4], [0.94, 1.04]);

  const titleOpacity = useTransform(scrollYProgress, [0, 0.25], [1, 0]);
  const titleY = useTransform(scrollYProgress, [0, 0.25], ["0vh", "-12vh"]);
  const hintOpacity = useTransform(scrollYProgress, [0, 0.06], [1, 0]);
  const glowY = useTransform(scrollYProgress, [0, 0.5], [0, -160]);

  return (
    <section ref={ref} className="relative flex h-screen flex-col items-center overflow-hidden bg-[#F8F9FA]" data-testid="hero-section">
      <div className="flex h-full w-full flex-col items-center">
        <motion.div
          style={{ y: glowY }}
          className="animate-glow pointer-events-none absolute left-1/2 top-1/2 h-[75vmin] w-[75vmin] -translate-x-1/2 -translate-y-1/3 rounded-full bg-[radial-gradient(circle,rgba(201,176,255,0.5)_0%,rgba(201,176,255,0.18)_45%,transparent_70%)] blur-3xl"
        />

        <motion.div
          style={{ opacity: titleOpacity, y: titleY }}
          className="pointer-events-none absolute inset-x-0 top-0 z-20 h-px"
          aria-hidden="true"
        />

        <motion.div
          style={{ y: cupY, opacity: cupOpacity, scale: cupScale, rotate: cupRotate }}
          className="absolute bottom-[7vh] z-10 w-[min(52vw,260px)] sm:w-[min(30vw,320px)]"
        >
          <div className="animate-glow pointer-events-none absolute left-1/2 top-1/2 h-[85%] w-[130%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(201,176,255,0.45)_0%,rgba(201,176,255,0.15)_45%,transparent_70%)] blur-2xl" />
          <div className="relative w-full" style={{ aspectRatio: ratio }} data-testid="hero-cup">
            <img
              src="/assets/cup.png"
              alt="Shake & Shake Aurora Cup"
              className="absolute inset-0 h-full w-full"
              style={{ filter: "drop-shadow(0 24px 50px rgba(155,122,232,0.4))" }}
              onLoad={(e) => setRatio(e.target.naturalWidth / e.target.naturalHeight)}
            />
            <div
              className="absolute inset-0"
              style={{
                WebkitMaskImage: "url('/assets/cup.png')",
                maskImage: "url('/assets/cup.png')",
                WebkitMaskSize: "100% 100%",
                maskSize: "100% 100%",
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
              }}
            >
              <motion.div
                className="absolute bottom-0 left-0 right-0"
                style={{ height: fillHeight }}
                data-testid="hero-liquid-fill"
              >
                <div className="relative h-full w-full bg-[linear-gradient(180deg,#E2D4FF_0%,#C9B0FF_45%,#9B7AE8_100%)] opacity-85 shadow-[0_0_60px_rgba(201,176,255,0.55)]">
                  <div className="absolute -top-2 left-0 right-0 h-4 rounded-[50%] bg-[#F0E8FF] blur-[3px]" />
                </div>
              </motion.div>
              <motion.div className="absolute inset-0" style={{ opacity: bubbleOpacity }}>
                {BUBBLES.map((b, i) => (
                  <motion.span
                    key={i}
                    className="absolute rounded-full bg-white shadow-[0_0_14px_rgba(255,255,255,0.9)]"
                    style={{ left: `${b.left}%`, bottom: "16%", width: b.size, height: b.size }}
                    animate={{ y: [0, -260 - b.drift * 4], x: [0, b.drift], opacity: [0, 1, 1, 0] }}
                    transition={{ duration: b.dur, delay: b.delay, repeat: Infinity, ease: "easeOut" }}
                  />
                ))}
              </motion.div>
            </div>
          </div>

          <div className="mt-4 flex justify-center">
            <div className="flex items-center gap-3 rounded-full border border-[#9B7AE8]/25 bg-white/80 px-5 py-2 shadow-[0_10px_30px_rgba(155,122,232,0.18)] backdrop-blur-md" data-testid="hero-prep-counter">
              <span className="h-2 w-2 animate-pulse rounded-full bg-[#9B7AE8] shadow-[0_0_10px_#9B7AE8]" />
              <span className="font-nav text-[11px] font-semibold uppercase tracking-[0.25em] text-[#6B6386]">
                {t.hero.counter} <motion.span className="text-[#9B7AE8]">{pct}</motion.span>%
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div
          style={{ opacity: hintOpacity }}
          className="absolute bottom-8 z-20 flex flex-col items-center gap-2"
          data-testid="hero-scroll-indicator"
        >
          <span className="font-nav text-[10px] font-medium uppercase tracking-[0.3em] text-[#8E85AC]">{t.hero.hint}</span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}>
            <ChevronDown size={18} className="text-[#9B7AE8]" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
